import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { RoutingService } from './routing.service';
import { SecurityHelperService } from './security-helper.service';
import { Authentication, ChangePasswordRequest, EmailConfirmation, ForgotPasswordRequest, User } from '../models';
import { PasswordReset } from '../models';
import { UserInfo } from '../models/user-info';
import { AdminChangePasswordRequest } from '../models/admin-change-password-request';
import { AccountInfo, AuthenticationResponse, MFASetupResult } from 'src/app/models';

@Injectable()
export class SecurityService {
  url: string = '';
  urlAccountSettings: string = '';
  
  private _hasJustLoggedIn = false;
  
  constructor(
    private httpClient: HttpClient,
    @Inject('API_URL') private apiUrl: string,
    private routingService: RoutingService,
    private securityHelperService: SecurityHelperService
  ) {
    this.url = `${this.apiUrl}account/`;
    this.urlAccountSettings = `${this.apiUrl}account-setting/`;
  }

  public get hasJustLoggedIn() { return this._hasJustLoggedIn; }
  public set hasJustLoggedIn(value: boolean) { this._hasJustLoggedIn = value; }

  authenticate(user: User): Observable<AuthenticationResponse> {
    const url = this.url + 'login';
    const response: Observable<AuthenticationResponse> = this.httpClient.post<AuthenticationResponse>(url, user);
    return response;
  }

  signOut(): void {
    this.securityHelperService.clearAuthenticationValues();
    this.routingService.navigateToLoginPage();
  }

  getUserInfo(): Observable<UserInfo> {
    let url: string = `${this.url}GetAccountInfo`;
    let response: Observable<UserInfo> = this.httpClient.get<UserInfo>(url);
    return response;
  }

  resetPassword(passwordReset: PasswordReset): Observable<any> {
    let url: string = this.url + 'password-reset';
    let response: Observable<any> = this.httpClient.post<any>(url, passwordReset);
    return response;
  }

  isLoggedIn(): Observable<boolean> {
    let url: string = `${this.url}IsAuthenticated`;
    let response: Observable<boolean> = this.httpClient.get<boolean>(url);
    return response;
  }

  public isSecurityComplaint(): Observable<boolean> {
    const url: string = this.apiUrl + 'account/IsSecurityCompliant';
    const response: Observable<boolean> = this.httpClient.get<boolean>(url);

    return response;
  }

  isAdministrator(): Observable<boolean> {
    let url: string = `${this.url}is-administrator`;
    let response: Observable<boolean> = this.httpClient.get<boolean>(url);
    return response;
  }

  isUser(): Observable<boolean> {
    let url: string = `${this.url}is-user`;
    let response: Observable<boolean> = this.httpClient.get<boolean>(url);
    return response;
  }

  isClient(): Observable<boolean> {
    let url: string = `${this.url}isclient`;
    let response: Observable<boolean> = this.httpClient.get<boolean>(url);
    return response;
  }

  resetPasswordRequest(passwordReset: PasswordReset): Observable<any> {
    let url: string = this.url + 'password-reset/request';
    let response: Observable<any> = this.httpClient.post(url, passwordReset);
    return response;
  }

  isEmailConfirmed(emailConfirmation: EmailConfirmation): Observable<boolean> {
    let url: string = this.url + 'is-email-confirmed';
    let response: Observable<boolean> = this.httpClient.post<boolean>(url, emailConfirmation);
    return response;
  }

  forgotPassword(email: string): Observable<Object> {
    const url = `${this.url}GeneratePasswordResetToken/`;

    const body: string = '"' + email + '"';
    const response: Observable<Object> = this.httpClient.post<Object>(url, body);
    return response;
  }

  sendConfirmationEmail(emailConfirmation: EmailConfirmation): Observable<any> {
    let url: string = this.url + 'send-confirmation-email';
    let response: Observable<boolean> = this.httpClient.post<any>(url, emailConfirmation);
    return response;
  }

  confirmAccount(emailConfirmation: EmailConfirmation): Observable<any> {
    let url: string = this.url + 'confirm-email';
    let response: Observable<any> = this.httpClient.post<any>(url, emailConfirmation);
    return response;
  }

  changePassword(changePassword: ChangePasswordRequest): Observable<any> {
    let url: string = this.url + 'change-password';
    let response: Observable<any> = this.httpClient.post<any>(url, changePassword);
    return response;
  }

  adminChangePassword(changePassword: AdminChangePasswordRequest): Observable<any> {
    let url: string = this.url + 'admin-change-password';
    let response: Observable<any> = this.httpClient.post<any>(url, changePassword);
    return response;
  }

  loginAsSubjectObliged(subjectObligedId: number, branchOfficeId: number | null): Observable<AuthenticationResponse> {
    const url: string = this.url + `LoginAsSubjectObliged/${subjectObligedId}?branchOfficeId=${branchOfficeId}`;
    const response: Observable<AuthenticationResponse> = this.httpClient.get<AuthenticationResponse>(url, {
      responseType: 'json',
    });
    return response;
  }

  isAdmin(): Observable<boolean> {
    let url: string = `${this.urlAccountSettings}isAdmin`;
    let response: Observable<boolean> = this.httpClient.get<boolean>(url);
    return response;
  }

  getAccountInfo(): Observable<AccountInfo> {
    const url: string = this.url + 'GetAccountInfo';
    const response: Observable<AccountInfo> = this.httpClient.get<AccountInfo>(url);
    return response;
  }

  setup2FAAuthenticator() {
    const url: string = this.url + 'Setup2FA';
    const response: Observable<MFASetupResult> = this.httpClient.post<MFASetupResult>(url, {});
    return response;
  }

  validate2FACode(code: string) {
    const url: string = this.url + 'Validate2FACode';
    const response: Observable<boolean> = this.httpClient.post<boolean>(url, { code: code });
    return response;
  }

  save2FAConfig(enabled: boolean) {
    const url: string = this.url + `Confirm2FASetup?enabled=${enabled}`;
    const response: Observable<boolean> = this.httpClient.put<boolean>(url, {});
    return response;
  }
}
